import React from 'react'

import Grid from '../../../../../components/pdf/Grid'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { HEADING_COLOR } from '../_helpers/brand'
import { usePortraitPageNumbers } from '../_helpers/pagination'

/**
 * Sumário (índice) — confirmado por Julia Buzzo como necessário (junto com as páginas
 * paisagem, ver `_shared/Landscape.js`).
 *
 * NÚMERO DE PÁGINA E SUBTÍTULOS: reintroduzidos nesta revisão a pedido explícito e
 * detalhado da Julia — REVERTE PARCIALMENTE o PR #731, que tinha removido a coluna de
 * número de página do Sumário (mantendo só título + link) por causa de um bug de
 * renderização ("0" fixo ao lado de todo item, ver a seguir) e tinha deixado o Sumário
 * listar só os títulos de nível 1 (sem subtítulos).
 *
 * O bug do "0" tinha uma causa raiz simples: o `IndexItem` compartilhado
 * (`components/pdf/IndexItem.js`, usado por outras marcas com números reais e NÃO
 * alterado aqui) tem `page = 0` como valor padrão, e `{page && <Text>{page}</Text>}`
 * avalia para `0` quando `page` não é informado — `0` é "falsy" em JS, mas ainda é
 * desenhado na tela pelo JSX (não é `null`/`undefined`/`false`). O `SumarioIndexItem`
 * abaixo, local deste arquivo, evita esse bug simplesmente nunca recebendo um `page`
 * padrão: sem uma entrada no mapa de `pageNumbers` (calculado por
 * `_helpers/pagination.js`, só depois que o documento inteiro terminou de se posicionar
 * na tela), a condição é `undefined && ...`, que nunca renderiza nada — não um `0`.
 *
 * NÚMEROS REAIS, NÃO ESTIMADOS: o motor de paginação (ver `_helpers/pagination.js` para o
 * mecanismo completo e suas premissas/limitações) mede a posição já renderizada de cada
 * âncora do documento (Seções 1-17 e seus subtítulos) DEPOIS que o layout terminou —
 * nunca um valor digitado à mão. `target-counter()`/`string-set` do CSS Paged Media (que
 * resolveria isso nativamente, referenciando a página real de cada âncora) foi verificado
 * empiricamente como NÃO suportado pelo motor de impressão do Chromium usado neste
 * pipeline (`page.pdf()`) — um teste local com `content: target-counter(url(#x), page)`
 * não renderizou nada no PDF gerado via Playwright/Chromium headless, nem o texto ao redor
 * do `target-counter()` (ver observação no PR). Por isso a medição é feita em JS, e as
 * Seções 18-21 (a partir da primeira página em paisagem, ver `_helpers/pagination.js`)
 * ficam DELIBERADAMENTE sem número — a técnica de medição depende de o conteúdo ser
 * renderizado na tela com a mesma largura da impressão (`Preview.js` já usa `21cm`,
 * equivalente a A4 retrato), premissa que deixa de valer a partir da paisagem.
 *
 * ENTRADAS CORRIGIDAS: a lista de seções abaixo também foi atualizada para bater com o
 * conteúdo real de cada arquivo (`numero`/título de `SectionHeading` e `id` de cada
 * `<View>`, ver `sections/01.*.js` a `sections/21.*.js`) — a lista anterior estava
 * defasada de uma renumeração já feita em `PdfDocument.js` (ver o histórico daquele
 * arquivo): tinha itens de texto "10. GRADAÇÃO DE PROBABILIDADE" / "11. GRADAÇÃO DE
 * SEVERIDADE" / "12. MATRIZ DE RISCO..." que não existem mais como tal (o conteúdo real
 * da Seção 10 hoje é "CLASSIFICAÇÃO DOS RISCOS: ERGONÔMICO/PSICOSSOCIAIS..."), e repetia
 * "11."/"12." duas vezes cada (uma delas sem link, por não ter `id` correspondente).
 * Precisava ser corrigido aqui de qualquer forma para a medição de página funcionar (não
 * dá para medir a posição de uma âncora que não existe) — não é um problema não
 * relacionado a esta tarefa, é a mesma tarefa: um Sumário com número de página real só
 * funciona se também apontar para o conteúdo real.
 *
 * "1. INFORMAÇÕES INCIAIS": grafia exatamente como no modelo real (erro de digitação
 * existente no PDF-modelo — falta o "I" de "INICIAIS"). Mantido igual em
 * `01.InformacoesIniciaisSection.js`. Os subtítulos "8.4"/"9.5" também reproduzem
 * exatamente a pontuação/grafia do texto-fonte (sem o "." depois do número, e "da" em vez
 * de "de" em "9.5") — mesma regra de não "corrigir, modernizar ou reescrever" texto do
 * modelo já documentada nas seções de origem.
 *
 * NUMERAÇÃO 16: a Seção 16 ("AVALIAÇÃO CONTÍNUA E REVISÃO DO PGR") está no mesmo arquivo
 * que a 15 (`16.RegistroControleAvaliacaoRevisaoSection.js`, ver o comentário daquele
 * arquivo) — agora tem sua própria âncora (`id="16"`, adicionada num `<div>` só para isso)
 * em vez de compartilhar a âncora da Seção 15, para que o link e a página no Sumário
 * apontem para o lugar certo.
 */

// IDs cuja posição pode ser medida com confiança (ver `_helpers/pagination.js`): tudo
// antes da primeira página em paisagem (Seção 18). A partir dali a premissa de largura de
// tela = largura de impressão deixa de valer, e a página calculada não seria confiável —
// por isso as Seções 18-21 (e não têm subtítulos, então é só o item de nível 1 de cada)
// simplesmente não entram nesta lista, e ficam sem número no Sumário.
const SUMARIO_ENTRIES = [
  { id: '1', level: 1, label: '1. INFORMAÇÕES INCIAIS', pageSafe: true },
  { id: '1.1', level: 2, label: '1.1. IDENTIFICAÇÃO DA UNIDADE', pageSafe: true },
  { id: '1.2', level: 2, label: '1.2. IDENTIFICAÇÃO DAS DEPENDÊNCIAS', pageSafe: true },

  { id: '2', level: 1, label: '2. INTRODUÇÃO', pageSafe: true },

  { id: '3', level: 1, label: '3. OBJETIVO', pageSafe: true },
  { id: '3.1', level: 2, label: '3.1. Objetivos específicos do Inventário de Riscos Ocupacionais', pageSafe: true },

  {
    id: '4',
    level: 1,
    label: '4. DIREITOS E DEVERES – RESPONSABILIDADES LEGAIS EM SEGURANÇA E SAÚDE NO TRABALHO',
    pageSafe: true
  },
  { id: '4.1', level: 2, label: '4.1. Responsabilidades do Empregador', pageSafe: true },
  { id: '4.2', level: 2, label: '4.2. Responsabilidades do Trabalhador', pageSafe: true },
  { id: '4.3', level: 2, label: '4.3. Melhoria Contínua do Desempenho em SST', pageSafe: true },
  {
    id: '4.4',
    level: 2,
    label: '4.4. Ações e Programas de Gerenciamento de Segurança, Saúde e Bem-Estar',
    pageSafe: true
  },

  { id: '5', level: 1, label: '5. METODOLOGIA', pageSafe: true },
  { id: '5.1', level: 2, label: '5.1. Identificação e Avaliação dos Riscos Ocupacionais', pageSafe: true },
  { id: '5.2', level: 2, label: '5.2. Riscos Ocupacionais Avaliados', pageSafe: true },
  { id: '5.3', level: 2, label: '5.3. Avaliação dos Riscos e Exposição dos Trabalhadores: Setores (GHE)', pageSafe: true },

  { id: '6', level: 1, label: '6. DESCRIÇÃO DAS ATIVIDADES', pageSafe: true },

  {
    id: '7',
    level: 1,
    label: '7. METODOLOGIA (FÍSICO/QUÍMICO/BIOLÓGICO/ERGONÔMICO-BIOMECÂNICO/ACIDENTE E ERGONÔMICO/PSICOSSOCIAL)',
    pageSafe: true
  },
  {
    id: '7.1',
    level: 2,
    label: '7.1. Metodologia: Físico, Químico, Biológico, Ergonômico/Biomecânico e Acidente',
    pageSafe: true
  },
  { id: '7.2', level: 2, label: '7.2. Metodologia: Ergonômico/Psicossocial', pageSafe: true },
  {
    id: '7.3',
    level: 2,
    label: '7.3. Avaliação e Gestão dos Fatores Psicossociais Relacionados ao Trabalho',
    pageSafe: true
  },

  { id: '8', level: 1, label: '8. CLASSIFICAÇÃO DOS RISCOS', pageSafe: true },
  { id: '8.1', level: 2, label: '8.1. Metodologia de Avaliação', pageSafe: true },
  { id: '8.2', level: 2, label: '8.2. Critério de Avaliação', pageSafe: true },
  { id: '8.3', level: 2, label: '8.3. Análise por Tipo de Fator de Risco', pageSafe: true },
  { id: '8.4', level: 2, label: '8.4 Classificação e Plano de Ação', pageSafe: true },

  {
    id: '9',
    level: 1,
    label: '9. CLASSIFICAÇÃO DOS RISCOS: FÍSICO, QUÍMICO, BIOLÓGICO, ERGONÔMICO AMBIENTAIS E ACIDENTE',
    pageSafe: true
  },
  { id: '9.1', level: 2, label: '9.1. Metodologia de Avaliação', pageSafe: true },
  { id: '9.2', level: 2, label: '9.2. Gradação de Probabilidade e Severidade', pageSafe: true },
  { id: '9.3', level: 2, label: '9.3. Matriz de Risco', pageSafe: true },
  { id: '9.4', level: 2, label: '9.4. Avaliação e Prioridade de Controle', pageSafe: true },
  { id: '9.5', level: 2, label: '9.5 Implementação da Medidas de Controle', pageSafe: true },

  {
    id: '10',
    level: 1,
    label: '10. CLASSIFICAÇÃO DOS RISCOS: ERGONÔMICO/PSICOSSOCIAIS E BIOMECÂNICOS',
    pageSafe: true
  },
  { id: '10.1', level: 2, label: '10.1. Metodologia de Avaliação', pageSafe: true },
  { id: '10.2', level: 2, label: '10.2. Gradação de Probabilidade e Severidade', pageSafe: true },
  { id: '10.3', level: 2, label: '10.3. Matriz de Risco', pageSafe: true },
  { id: '10.4', level: 2, label: '10.4. Avaliação e Prioridade de Controle', pageSafe: true },
  { id: '10.5', level: 2, label: '10.5. Implementação das Medidas de Controle', pageSafe: true },

  { id: '11', level: 1, label: '11. PLANO DE AÇÃO: DEFINIÇÃO E APLICABILIDADE', pageSafe: true },
  { id: '12', level: 1, label: '12. MONITORAMENTO DA EXPOSIÇÃO', pageSafe: true },
  { id: '13', level: 1, label: '13. PLANO DE RESPOSTA A EMERGÊNCIAS', pageSafe: true },
  { id: '14', level: 1, label: '14. GESTÃO PARTICIPATIVA: CIPA E CAPACITAÇÃO', pageSafe: true },
  { id: '15', level: 1, label: '15. REGISTRO E CONTROLE DAS INFORMAÇÕES', pageSafe: true },
  { id: '16', level: 1, label: '16. AVALIAÇÃO CONTÍNUA E REVISÃO DO PGR', pageSafe: true },
  { id: '17', level: 1, label: '17. GESTÃO DE EMPRESAS TERCEIRAS', pageSafe: true },

  // A partir daqui, páginas em paisagem — ver comentário acima e em
  // `_helpers/pagination.js`: sem número de página confiável, por isso `pageSafe: false`.
  { id: '18', level: 1, label: '18. INVENTÁRIO DE RISCOS', pageSafe: false },
  { id: '19', level: 1, label: '19. PLANO DE AÇÃO', pageSafe: false },
  { id: '20', level: 1, label: '20. CONSIDERAÇÕES FINAIS', pageSafe: false },
  { id: '21', level: 1, label: '21. CERTIFICADO DE CALIBRAÇÃO', pageSafe: false }
]

const SAFE_PAGE_NUMBER_ANCHOR_IDS = SUMARIO_ENTRIES.filter(entry => entry.pageSafe).map(entry => entry.id)

// `lineHeight` compacto (o padrão de `components/pdf/Text.js` é 1.5, pensado para corpo
// de texto corrido) — com 44 entradas (17 títulos + 27 subtítulos, ver `SUMARIO_ENTRIES`)
// o Sumário completo com o espaçamento padrão não cabe numa página só junto com o
// "Controle de Revisões" logo abaixo (exigência explícita da Julia, ver comentário no
// topo do arquivo) — cada linha aqui é só uma entrada de índice, não texto de leitura.
const ITEM_STYLE = { lineHeight: 1.2 }

/**
 * Um item do Sumário. Nunca renderiza a coluna de página quando `pageNumber` não é um
 * número (não existe um valor padrão "0"-like aqui, ao contrário do `IndexItem`
 * compartilhado — ver o comentário no topo do arquivo). Estrutura (link > `Grid` de
 * título/linha pontilhada/página) segue o mesmo padrão já usado pelo `IndexItem`
 * compartilhado (`components/pdf/IndexItem.js`) nos outros PDFs, só com `level` (para o
 * indent/tamanho do subtítulo) e a proteção do `0` a mais.
 */
const SumarioIndexItem = ({ label, name, level, pageNumber }) => {
  const isSubtitle = level === 2
  const size = isSubtitle ? 'xsmall' : 'small'

  return (
    <a
      style={{ textDecoration: 'none', display: 'block', paddingLeft: isSubtitle ? 16 : 0 }}
      href={name && `#${name}`}
    >
      <Grid align="flex-end">
        <Text size={size} style={{ ...ITEM_STYLE, flexShrink: 0, maxWidth: '80%' }}>
          {label}
        </Text>
        <div style={{ flex: 1, minWidth: 12, margin: '0 6px', borderBottom: '1px dotted #666666' }} />
        {typeof pageNumber === 'number' && (
          <Text size={size} style={ITEM_STYLE}>
            {pageNumber}
          </Text>
        )}
      </Grid>
    </a>
  )
}

const SumarioSection = () => {
  const pageNumbers = usePortraitPageNumbers(SAFE_PAGE_NUMBER_ANCHOR_IDS)

  return (
    <View>
      <Separator weight={15} />
      <Text bold size="small" color={HEADING_COLOR} align="center">
        Sumário
      </Text>
      <Separator weight={8} />

      {SUMARIO_ENTRIES.map(entry => (
        <SumarioIndexItem
          key={entry.id}
          label={entry.label}
          name={entry.id}
          level={entry.level}
          pageNumber={pageNumbers[entry.id]}
        />
      ))}

      <Separator weight={15} />
    </View>
  )
}

export default SumarioSection
